/**
 * Netly Pay settings — payment methods, approval rules, reminders, staff and
 * the join form, behind a left sub-navigation.
 *
 * Everything saves on change rather than behind a Save button, except the
 * payment methods, where turning the last one off is refused: requests nobody
 * can pay are worse than no requests. The rule lives in `paymentMethodsError`
 * so the Enable wizard and this screen cannot disagree about it.
 *
 * Spec: DESIGN-BRIEF.md §J and `vSettings()` in prototype.html.
 */
import React, {useMemo, useState} from 'react';
import {connect} from 'react-redux';
import NetlyPayRepository from '../../../internals/repository/NetlyPayRepository';
import {
  APPROVAL_RULES,
  BANK_FIELDS,
  DEFAULT_SETTINGS,
  REMIND_AFTER,
  REMIND_BEFORE,
  ROLE_CARDS,
  SETTINGS_NAV,
  afterLabel,
  beforeLabel,
  cycleJoinField,
  inviteError,
  joinFieldRows,
  joinLink,
  paymentMethodsError,
  posterCaption,
  reminderPlan,
  smsPreview,
  staffRows,
  toggleAfter,
  type JoinFieldState,
  type NetlyPaySettings,
  type SettingsSection,
  type Staff,
} from '../settings';
import {useNetlyPayFetch, useNetlyPayLink} from '../hooks/useNetlyPay';
import {ErrorState, Skeleton} from '../components/States';
import {GhostButton, PrimaryButton} from '../components/Overlays';
import {ScreenGate, Toast} from '../components/ScreenGate';

const inputCls =
  'h-10 px-3 rounded-lg border border-borderc2 bg-surface text-[13px] text-text1 outline-none focus:border-brand transition-colors';
const sectionTitle = 'text-[16px] font-semibold text-text1';
const fieldLabel = 'text-[12px] font-semibold text-text2';

const Switch = ({on, onClick, title}: {on: boolean; onClick: () => void; title: string}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={title}
    onClick={onClick}
    className={`w-[38px] h-[22px] rounded-full p-[3px] flex-shrink-0 transition-colors ${
      on ? 'bg-brand' : 'bg-surface3'
    }`}
  >
    <span
      className={`block w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : ''}`}
      style={{boxShadow: '0 1px 2px rgba(15,15,26,.3)'}}
    />
  </button>
);

const Row = ({
  title,
  text,
  on,
  onToggle,
}: {
  title: string;
  text: string;
  on: boolean;
  onToggle: () => void;
}) => (
  <div className="flex items-start justify-between gap-4 px-4 py-3.5 rounded-xl border border-borderc bg-surface">
    <span>
      <span className="block text-[13px] font-semibold text-text1">{title}</span>
      <span className="block text-[12px] text-text2 mt-0.5">{text}</span>
    </span>
    <Switch on={on} onClick={onToggle} title={title} />
  </div>
);

const Chip = ({
  label,
  on,
  onClick,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    className={`h-8 px-3 rounded-full text-[12.5px] font-medium border transition-colors ${
      on ? 'bg-brand-soft border-brand text-brand' : 'bg-surface border-borderc text-text2 hover:bg-surface2'
    }`}
  >
    {label}
  </button>
);

export const NetlyPaySettingsView = ({selectedShop}: any) => {
  const shopId = selectedShop?.shopId;
  const shopName = selectedShop?.shopName ?? selectedShop?.name ?? 'Your shop';
  const currency = selectedShop?.currency ?? 'Rs.';
  const link = useNetlyPayLink(shopId);

  const [section, setSection] = useState<SettingsSection>('methods');
  const [draft, setDraft] = useState<NetlyPaySettings | null>(null);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [joinFields, setJoinFields] = useState<Record<string, JoinFieldState>>({});
  const [invite, setInvite] = useState('');
  const [inviteTried, setInviteTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2800);
  };

  const loaded = useNetlyPayFetch<any>(
    link.phase === 'linked' ? shopId : null,
    async (id) => {
      // Settings are fields of the workspace record, and staff is its own
      // route — there is no /settings endpoint on the Pay API.
      const wsId = link.workspace?.id ?? '';
      const [ws, team] = await Promise.all([
        NetlyPayRepository.getSettings(id, wsId),
        NetlyPayRepository.listStaff(id, wsId).catch(() => ({items: [], invites: []})),
      ]);
      const w = ws?.workspace ?? ws ?? {};
      const settings: NetlyPaySettings = {...DEFAULT_SETTINGS, ...(w.settings ?? {})};
      setDraft(settings);
      setStaff([
        ...(team?.items ?? []).map((m: any) => ({
          id: m.userId,
          name: m.name || m.email || 'Staff',
          email: m.email,
          role: m.role === 'OWNER' ? 'Owner' : m.title || 'Coach',
        })),
        ...(team?.invites ?? []).map((i: any) => ({
          id: i.inviteId ?? i.code,
          name: i.email,
          email: i.email,
          role: i.title || 'Coach',
          invited: true,
        })),
      ] as any);
      setJoinFields(w.joinFields ?? settings.joinFields ?? {});
      return w;
    },
    [link.phase],
  );

  const settings = draft ?? DEFAULT_SETTINGS;
  const methodsError = paymentMethodsError(settings);
  const rows = useMemo(() => staffRows(staff), [staff]);
  const fields = useMemo(() => joinFieldRows(joinFields), [joinFields]);
  const sms = useMemo(
    () =>
      smsPreview(
        shopName,
        {title: 'October fee', amountMinor: 800000, due: '2026-10-25'},
        currency,
      ),
    [shopName, currency],
  );
  const inviteProblem = inviteError(invite, staff);

  /**
   * Save a change, keeping what is on screen.
   *
   * The local state is updated first so a toggle does not lag behind the
   * finger, and rolled back if the API refuses — the alternative, waiting for
   * the round trip, makes five toggles feel broken.
   */
  const patch = async (next: Partial<NetlyPaySettings>, blocked?: string | null) => {
    if (!shopId || !draft) return;
    if (blocked) {
      flash(blocked);
      return;
    }
    const before = draft;
    const after = {...draft, ...next};
    setDraft(after);
    try {
      await NetlyPayRepository.updateSettings(shopId, link.workspace?.id ?? '', next as Record<string, any>);
    } catch (e: any) {
      setDraft(before);
      flash(e?.message ?? 'Could not save that change');
    }
  };

  const setJoinField = async (key: string, state: JoinFieldState) => {
    const next = {...joinFields, [key]: state};
    setJoinFields(next);
    if (!shopId) return;
    try {
      await NetlyPayRepository.updateSettings(shopId, link.workspace?.id ?? '', {joinFields: next});
    } catch (e: any) {
      setJoinFields(joinFields);
      flash(e?.message ?? 'Could not save that change');
    }
  };

  const sendInvite = async () => {
    setInviteTried(true);
    if (!shopId || inviteProblem) return;
    setBusy(true);
    try {
      await NetlyPayRepository.inviteStaff(shopId, link.workspace?.id ?? '', {email: invite.trim(), role: 'Coach'});
      setStaff(staff.concat([{name: invite.trim().split('@')[0], email: invite.trim(), role: 'Coach', invited: true}]));
      flash(`Invite sent to ${invite.trim()}`);
      setInvite('');
      setInviteTried(false);
    } catch (e: any) {
      flash(e?.message ?? 'Could not send the invite');
    } finally {
      setBusy(false);
    }
  };

  /**
   * The Pay API has no route that changes a role: it is chosen when the invite
   * is sent, and the only other move is taking access away. So 'None' removes
   * the person, and the other buttons say why they do nothing rather than
   * appearing to save.
   */
  const setRole = async (id: string, role: Staff['role']) => {
    if (!shopId) return;
    if (role !== 'None') {
      flash('A role is set when the invite is sent. Remove and re-invite to change it.');
      return;
    }
    const before = staff;
    setStaff(staff.filter((p, i) => (p.id ?? p.email ?? String(i)) !== id));
    try {
      await NetlyPayRepository.removeStaff(shopId, link.workspace?.id ?? '', id);
      flash('Access removed');
    } catch (e: any) {
      setStaff(before);
      flash(e?.message ?? 'Could not remove that access');
    }
  };

  const copyLink = async () => {
    const url = joinLink(link.workspace?.id ?? '');
    try {
      await navigator.clipboard.writeText(url);
      flash('Join link copied');
    } catch (e) {
      flash(url);
    }
  };

  const bankReady = settings.bankOn;

  return (
    <ScreenGate link={link} shopId={shopId} shop={selectedShop}>
      <div className="flex flex-col w-full p-7 gap-5 nl-rise">
        <div>
          <h1 className="text-[26px] font-semibold text-text1 tracking-[-0.01em] leading-tight">
            Netly Pay settings
          </h1>
          <p className="text-[13px] text-text2 mt-1">
            How members pay, what staff may do, and what the join form asks for.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-5 items-start">
          {/* Sub-navigation */}
          <nav className="bg-surface border border-borderc rounded-card p-2 flex flex-col gap-0.5">
            {SETTINGS_NAV.map((n) => {
              const on = section === n.key;
              return (
                <button
                  key={n.key}
                  type="button"
                  onClick={() => setSection(n.key)}
                  aria-current={on ? 'page' : undefined}
                  className={`text-left px-3 py-2.5 rounded-lg text-[13px] transition-colors ${
                    on ? 'bg-brand-soft text-brand font-semibold' : 'text-text2 hover:bg-surface2'
                  }`}
                >
                  {n.label}
                </button>
              );
            })}
          </nav>

          <div className="bg-surface border border-borderc rounded-card p-6">
            {loaded.phase === 'loading' && <Skeleton rows={5} />}
            {loaded.phase === 'error' && (
              <ErrorState message={loaded.error} onRetry={loaded.reload} />
            )}

            {loaded.phase !== 'loading' && loaded.phase !== 'error' && (
              <>
                {/* Payment methods */}
                {section === 'methods' && (
                  <section className="flex flex-col gap-4 max-w-[620px]">
                    <h2 className={sectionTitle}>Payment methods</h2>
                    {methodsError && (
                      <p className="text-[12.5px] text-neg">{methodsError}</p>
                    )}

                    <div className="rounded-xl border border-borderc bg-surface">
                      <div className="flex items-start justify-between gap-4 px-4 py-3.5">
                        <span>
                          <span className="block text-[13px] font-semibold text-text1">
                            Accept bank transfers
                          </span>
                          <span className="block text-[12px] text-text2 mt-0.5">
                            Members see these details and upload a slip.
                          </span>
                        </span>
                        <Switch
                          on={settings.bankOn}
                          title="Accept bank transfers"
                          onClick={() =>
                            patch(
                              {bankOn: !settings.bankOn},
                              !settings.bankOn
                                ? null
                                : paymentMethodsError({...settings, bankOn: false}),
                            )
                          }
                        />
                      </div>
                      {bankReady && (
                        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {BANK_FIELDS.map((f) => (
                            <label key={f.key} className="flex flex-col gap-1.5">
                              <span className={fieldLabel}>{f.label}</span>
                              <input
                                value={settings.bank[f.key] ?? ''}
                                onChange={(e) => setDraft({...settings, bank: {...settings.bank, [f.key]: e.target.value}})}
                                onBlur={() => patch({bank: settings.bank})}
                                className={`${inputCls} ${f.mono ? 'font-mono' : ''}`}
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <Row
                      title="Accept LankaQR"
                      text="Show your QR to members in the app."
                      on={settings.lankaqr}
                      onToggle={() =>
                        patch(
                          {lankaqr: !settings.lankaqr},
                          settings.lankaqr
                            ? paymentMethodsError({...settings, lankaqr: false})
                            : null,
                        )
                      }
                    />
                    <Row
                      title="Accept cash at the desk"
                      text="Front desk and the POS can record cash."
                      on={settings.cash}
                      onToggle={() =>
                        patch(
                          {cash: !settings.cash},
                          settings.cash ? paymentMethodsError({...settings, cash: false}) : null,
                        )
                      }
                    />

                    {settings.lankaqr && (
                      <div className="rounded-xl border border-borderc bg-surface2 px-4 py-4">
                        <p className={fieldLabel}>LANKAQR IMAGE</p>
                        <div
                          className="mt-2.5 w-[150px] h-[150px] rounded-xl border border-borderc"
                          style={{
                            background:
                              'repeating-linear-gradient(135deg, var(--surface) 0 8px, var(--surface3) 8px 16px)',
                          }}
                          aria-label="LankaQR image placeholder"
                        />
                        <p className="text-[12px] text-text3 mt-2.5">
                          Upload the QR your bank issued. Members scan it in the app.
                        </p>
                      </div>
                    )}
                  </section>
                )}

                {/* Approval rules */}
                {section === 'rules' && (
                  <section className="flex flex-col gap-3 max-w-[620px]">
                    <h2 className={sectionTitle}>Approval rules</h2>
                    {APPROVAL_RULES.map((r) => (
                      <Row
                        key={r.key}
                        title={r.title}
                        text={r.text}
                        on={!!settings[r.key]}
                        onToggle={() => patch({[r.key]: !settings[r.key]} as Partial<NetlyPaySettings>)}
                      />
                    ))}
                  </section>
                )}

                {/* Reminders */}
                {section === 'reminders' && (
                  <section className="flex flex-col gap-5 max-w-[620px]">
                    <h2 className={sectionTitle}>Reminders</h2>

                    <div>
                      <p className={fieldLabel}>BEFORE THE DUE DATE</p>
                      <div className="flex items-center gap-1.5 flex-wrap mt-2">
                        {REMIND_BEFORE.map((n) => (
                          <Chip
                            key={n}
                            label={beforeLabel(n)}
                            on={settings.remindBefore === n}
                            onClick={() => patch({remindBefore: n})}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className={fieldLabel}>AFTER IT</p>
                      <div className="flex items-center gap-1.5 flex-wrap mt-2">
                        {REMIND_AFTER.map((n) => {
                          const on = settings.remindAfter.includes(n);
                          return (
                            <Chip
                              key={n}
                              label={afterLabel(n, on)}
                              on={on}
                              onClick={() => patch({remindAfter: toggleAfter(settings.remindAfter, n)})}
                            />
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className={fieldLabel}>CHANNEL</p>
                      <div className="inline-flex p-[3px] rounded-[10px] bg-surface3 gap-0.5 mt-2">
                        {(['App only', 'App and SMS'] as const).map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => patch({remindChannel: c})}
                            className={`h-8 px-3.5 rounded-lg text-[12.5px] font-semibold transition-colors ${
                              settings.remindChannel === c ? 'bg-surface text-text1 shadow-sm' : 'text-text3'
                            }`}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    </div>

                    <p className="text-[12.5px] text-text2">{reminderPlan(settings)}</p>

                    <div className="rounded-xl border border-borderc bg-surface2 px-4 py-4">
                      <p className={fieldLabel}>SMS PREVIEW</p>
                      <p className="text-[13px] text-text1 mt-2 leading-relaxed">{sms.text}</p>
                      <p
                        className={`text-[11.5px] font-mono mt-2 ${
                          sms.overLimit ? 'text-warn font-semibold' : 'text-text3'
                        }`}
                      >
                        {sms.note}
                      </p>
                    </div>
                  </section>
                )}

                {/* Staff and roles */}
                {section === 'staff' && (
                  <section className="flex flex-col gap-5">
                    <h2 className={sectionTitle}>Staff and roles</h2>

                    <div className="rounded-xl border border-borderc overflow-hidden">
                      {rows.length === 0 && (
                        <p className="px-4 py-5 text-[13px] text-text3">
                          Nobody else has access yet. Invite a coach or the front desk below.
                        </p>
                      )}
                      {rows.map((r) => (
                        <div
                          key={r.id}
                          className="flex items-center justify-between gap-4 px-4 py-3 border-b border-borderc last:border-b-0 flex-wrap"
                        >
                          <span className="min-w-0">
                            <span className="block text-[13px] font-semibold text-text1 truncate">
                              {r.name}
                            </span>
                            <span className="block text-[12px] text-text3 truncate">{r.email}</span>
                          </span>
                          <span className="inline-flex p-[3px] rounded-[9px] bg-surface3 gap-0.5">
                            {r.options.map((o) => (
                              <button
                                key={o}
                                type="button"
                                disabled={r.locked}
                                onClick={() => setRole(r.id, o)}
                                className={`h-7 px-2.5 rounded-md text-[12px] font-semibold transition-colors disabled:cursor-default ${
                                  r.role === o ? 'bg-surface text-text1 shadow-sm' : 'text-text3'
                                }`}
                              >
                                {o}
                              </button>
                            ))}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col gap-2 max-w-[460px]">
                      <p className={fieldLabel}>INVITE SOMEONE</p>
                      <div className="flex items-center gap-2.5">
                        <input
                          value={invite}
                          onChange={(e) => setInvite(e.target.value)}
                          placeholder="coach@example.com"
                          aria-label="Email address to invite"
                          className={`${inputCls} flex-1 ${
                            inviteTried && inviteProblem ? 'border-neg' : ''
                          }`}
                        />
                        <PrimaryButton disabled={busy} onClick={sendInvite}>
                          Send invite
                        </PrimaryButton>
                      </div>
                      {inviteTried && inviteProblem && (
                        <p className="text-[12px] text-neg">{inviteProblem}</p>
                      )}
                      <p className="text-[12px] text-text3">
                        They join as a coach. Change the role once they have accepted.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {ROLE_CARDS.map((c) => (
                        <div key={c.title} className="rounded-xl border border-borderc bg-surface2 px-4 py-3.5">
                          <p className="text-[13px] font-semibold text-text1">{c.title}</p>
                          <p className="text-[12px] text-text2 mt-1">{c.text}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Join form and QR */}
                {section === 'join' && (
                  <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-6 items-start">
                    <div className="flex flex-col gap-3">
                      <h2 className={sectionTitle}>Join form</h2>
                      <p className="text-[12.5px] text-text2">
                        Tap a field to cycle it between Required, Optional and Hidden.
                      </p>
                      <div className="rounded-xl border border-borderc overflow-hidden">
                        {fields.map((f) => (
                          <div
                            key={f.key}
                            className="flex items-center justify-between gap-4 px-4 py-3 border-b border-borderc last:border-b-0"
                          >
                            <span className="min-w-0">
                              <span className="block text-[13px] font-medium text-text1">{f.label}</span>
                              {f.sub && <span className="block text-[11.5px] text-text3">{f.sub}</span>}
                            </span>
                            <button
                              type="button"
                              disabled={f.locked}
                              onClick={() => setJoinField(f.key, cycleJoinField(f.state))}
                              aria-label={`${f.label} is ${f.state}`}
                              className={`h-8 px-3 rounded-full text-[12px] font-semibold border transition-colors disabled:cursor-default ${
                                f.tone === 'accent'
                                  ? 'bg-brand-soft border-brand/40 text-brand'
                                  : f.tone === 'muted'
                                  ? 'bg-surface3 border-borderc text-text3'
                                  : 'bg-surface border-borderc2 text-text1'
                              }`}
                            >
                              {f.state}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <aside className="rounded-xl border border-borderc bg-surface2 px-4 py-4">
                      <p className={fieldLabel}>PRINTABLE POSTER</p>
                      <div className="mt-3 rounded-xl bg-surface border border-borderc px-4 py-5 text-center">
                        <p className="text-[13px] font-semibold text-text1">{shopName}</p>
                        <div
                          className="mx-auto mt-3 w-[140px] h-[140px] rounded-xl border border-borderc"
                          style={{
                            background:
                              'repeating-linear-gradient(135deg, var(--surface) 0 8px, var(--surface3) 8px 16px)',
                          }}
                          aria-label="Join QR code placeholder"
                        />
                        <p className="text-[12px] text-text2 mt-3">{posterCaption(shopName)}</p>
                      </div>
                      <div className="flex flex-col gap-2 mt-3">
                        <GhostButton onClick={() => window.print()}>Print poster</GhostButton>
                        <GhostButton onClick={copyLink}>Copy link</GhostButton>
                      </div>
                      <p className="text-[11.5px] font-mono text-text3 mt-2.5 break-all">
                        {joinLink(link.workspace?.id ?? '')}
                      </p>
                    </aside>
                  </section>
                )}
              </>
            )}
          </div>
        </div>

        {toast && <Toast message={toast} />}
      </div>
    </ScreenGate>
  );
};

export default connect((state: any) => ({
  selectedShop: state.dashboard.get('selectedShop'),
}))(NetlyPaySettingsView);
